/**
 * useScopeGraph — GraphQL hook for scope DAG data (DRR-075).
 *
 * Follows useDAGExecution pattern: query + polling + refetch.
 * Fetches scope graph, scope path, and mutations from backend.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { notifications } from '@mantine/notifications';

// ---------------------------------------------------------------------------
// GraphQL Operations
// ---------------------------------------------------------------------------

const SCOPE_GRAPH_QUERY = gql`
  query ScopeGraph {
    scopeGraph {
      tenantId
      edges {
        sourceName
        targetName
        edgeType
        effectiveFrom
        effectiveUntil
      }
      nodeCount
      edgeCount
      roots
      leaves
    }
  }
`;

const SCOPE_PATH_QUERY = gql`
  query ScopePath($actorId: String!) {
    scopePath(actorId: $actorId) {
      actorId
      path
      depth
    }
  }
`;

const SCOPE_MUTATIONS_QUERY = gql`
  query ScopeMutations($limit: Int) {
    scopeMutations(limit: $limit) {
      id
      edge {
        sourceName
        targetName
        edgeType
      }
      mutationKind
      performedBy
      timestamp
      sequence
      hash
      previousHash
    }
  }
`;

const ADD_SCOPE_EDGE_MUTATION = gql`
  mutation AddScopeEdge($input: AddScopeEdgeInput!) {
    addScopeEdge(input: $input) {
      id
      edge {
        sourceName
        targetName
        edgeType
      }
      mutationKind
      performedBy
      timestamp
      sequence
      hash
    }
  }
`;

const REMOVE_SCOPE_EDGE_MUTATION = gql`
  mutation RemoveScopeEdge($input: RemoveScopeEdgeInput!) {
    removeScopeEdge(input: $input) {
      id
      mutationKind
      timestamp
      hash
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScopeEdge {
  /** Display name of the source actor — never a raw sequential ID */
  sourceName: string;
  /** Display name of the target actor — never a raw sequential ID */
  targetName: string;
  edgeType: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

export interface ScopeGraphData {
  tenantId: string;
  edges: ScopeEdge[];
  nodeCount: number;
  edgeCount: number;
  roots: string[];
  leaves: string[];
}

export interface ScopePathData {
  actorId: string;
  path: string[];
  depth: number;
}

export interface ScopeMutationRecord {
  id: string;
  edge: {
    /** Display name — never a raw sequential ID */
    sourceName: string;
    /** Display name — never a raw sequential ID */
    targetName: string;
    edgeType: string;
  };
  mutationKind: string;
  performedBy: string;
  timestamp: string;
  sequence: string;
  hash: string;
  previousHash: string | null;
}

export interface AddScopeEdgeInput {
  sourceActorId: string;
  targetActorId: string;
  edgeType: string;
}

export interface RemoveScopeEdgeInput {
  sourceActorId: string;
  targetActorId: string;
  edgeType: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseScopeGraphProps {
  pollingInterval?: number;
  autoRefresh?: boolean;
}

export interface UseScopeGraphReturn {
  graph: ScopeGraphData | null;
  scopePath: ScopePathData | null;
  mutations: ScopeMutationRecord[];
  isLoading: boolean;
  error: Error | null;
  fetchScopePath: (actorId: string) => void;
  addEdge: (input: AddScopeEdgeInput) => Promise<boolean>;
  removeEdge: (input: RemoveScopeEdgeInput) => Promise<boolean>;
  refreshGraph: () => void;
  refreshMutations: () => void;
}

export function useScopeGraph(
  props?: UseScopeGraphProps
): UseScopeGraphReturn {
  const { pollingInterval = 5000, autoRefresh = false } = props || {};

  const [scopePath, setScopePath] = useState<ScopePathData | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Graph query
  const {
    data: graphData,
    error: graphError,
    loading: graphLoading,
    refetch: refetchGraph,
  } = useQuery<{ scopeGraph: ScopeGraphData }>(SCOPE_GRAPH_QUERY);

  // Mutations audit trail
  const {
    data: mutationsData,
    error: mutationsError,
    loading: mutationsLoading,
    refetch: refetchMutations,
  } = useQuery<{ scopeMutations: ScopeMutationRecord[] }>(
    SCOPE_MUTATIONS_QUERY,
    { variables: { limit: 50 } }
  );

  // Scope path (lazy)
  const {
    refetch: refetchPath,
  } = useQuery<{ scopePath: ScopePathData }>(SCOPE_PATH_QUERY, {
    skip: true,
  });

  // Mutations
  const [addEdgeMutation] = useMutation<{
    addScopeEdge: ScopeMutationRecord;
  }>(ADD_SCOPE_EDGE_MUTATION);
  const [removeEdgeMutation] = useMutation<{
    removeScopeEdge: ScopeMutationRecord;
  }>(REMOVE_SCOPE_EDGE_MUTATION);

  // Polling
  useEffect(() => {
    if (!autoRefresh) return undefined;
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      void refetchGraph();
    }, pollingInterval);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [autoRefresh, pollingInterval, refetchGraph]);

  const fetchScopePath = useCallback(
    async (actorId: string) => {
      try {
        const { data } = await refetchPath({ actorId });
        if (data?.scopePath) {
          setScopePath(data.scopePath);
        }
      } catch (err) {
        console.error('Failed to fetch scope path:', err);
      }
    },
    [refetchPath]
  );

  const addEdge = useCallback(
    async (input: AddScopeEdgeInput): Promise<boolean> => {
      try {
        const { data } = await addEdgeMutation({ variables: { input } });
        if (data?.addScopeEdge) {
          notifications.show({
            title: 'Scope Edge Added',
            message: `Edge added (${input.edgeType})`,
            color: 'green',
          });
          void refetchGraph();
          void refetchMutations();
          return true;
        }
        return false;
      } catch (err) {
        notifications.show({
          title: 'Failed to Add Edge',
          message:
            err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
        return false;
      }
    },
    [addEdgeMutation, refetchGraph, refetchMutations]
  );

  const removeEdge = useCallback(
    async (input: RemoveScopeEdgeInput): Promise<boolean> => {
      try {
        const { data } = await removeEdgeMutation({
          variables: { input },
        });
        if (data?.removeScopeEdge) {
          notifications.show({
            title: 'Scope Edge Removed',
            message: `Edge removed (${input.edgeType})`,
            color: 'yellow',
          });
          void refetchGraph();
          void refetchMutations();
          return true;
        }
        return false;
      } catch (err) {
        notifications.show({
          title: 'Failed to Remove Edge',
          message:
            err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
        return false;
      }
    },
    [removeEdgeMutation, refetchGraph, refetchMutations]
  );

  return {
    graph: graphData?.scopeGraph ?? null,
    scopePath,
    mutations: mutationsData?.scopeMutations ?? [],
    isLoading: graphLoading || mutationsLoading,
    error: graphError ?? mutationsError ?? null,
    fetchScopePath,
    addEdge,
    removeEdge,
    refreshGraph: refetchGraph,
    refreshMutations: refetchMutations,
  };
}
